import { useMatchesContext } from "@/contexts/TournamentContext";
import { detectWinner } from "@/lib/sports";
import { MatchStatus, PadelMatchResult, TeamStatistics } from "@/types";
import { useMemo } from "react";

/**
 * Computes statistics for ALL teams in a single pass through matches.
 * This replaces per-team useTeamStatistics calls to avoid O(teams × matches) complexity.
 *
 * @returns Map of teamId -> TeamStatistics
 */
export function useAllTeamStatistics(): Map<string, TeamStatistics> {
  const { matches } = useMatchesContext();

  return useMemo(() => {
    const statsMap = new Map<string, TeamStatistics>();

    if (!matches || matches.length === 0) {
      return statsMap;
    }

    // Helper to get or create stats for a team
    const getStats = (teamId: string): TeamStatistics => {
      let stats = statsMap.get(teamId);
      if (!stats) {
        stats = {
          teamId,
          wins: 0,
          losses: 0,
          winRate: 0,
          challengesSent: 0,
          challengesReceived: 0,
          gamesWon: 0,
          gamesLost: 0,
          setsWon: 0,
          setsLost: 0,
          tiebreaksWon: 0,
          tiebreaksLost: 0,
          rankingHistory: [],
        };
        statsMap.set(teamId, stats);
      }
      return stats;
    };

    // Single pass through all completed matches
    const completedMatches = matches.filter(
      (match) => match.status === MatchStatus.COMPLETED,
    );

    for (const match of completedMatches) {
      const team1Stats = getStats(match.team1Id);
      const team2Stats = getStats(match.team2Id);

      // Determine winner
      const winnerId = match.result?.walkover
        ? match.result.winnerId
        : detectWinner(match, match.result as any);

      if (winnerId === match.team1Id) {
        team1Stats.wins += 1;
        team2Stats.losses += 1;
      } else if (winnerId === match.team2Id) {
        team2Stats.wins += 1;
        team1Stats.losses += 1;
      }

      // Skip detailed stats for walkover matches (no actual games played)
      if (match.result?.walkover) continue;

      // Calculate detailed stats from match result
      if (match.result && match.sport === "padel") {
        const result = match.result as PadelMatchResult;

        const sets = [result.set1, result.set2, result.set3].filter(
          (set) => set.team1Games > 0 || set.team2Games > 0,
        );

        for (const set of sets) {
          // Team 1 stats
          team1Stats.gamesWon += set.team1Games;
          team1Stats.gamesLost += set.team2Games;

          // Team 2 stats
          team2Stats.gamesWon += set.team2Games;
          team2Stats.gamesLost += set.team1Games;

          // Sets won/lost
          if (set.team1Games > set.team2Games) {
            team1Stats.setsWon += 1;
            team2Stats.setsLost += 1;
          } else if (set.team2Games > set.team1Games) {
            team2Stats.setsWon += 1;
            team1Stats.setsLost += 1;
          }

          // Tiebreaks
          if (set.tieBreak) {
            if (set.tieBreak.team1Points > set.tieBreak.team2Points) {
              team1Stats.tiebreaksWon += 1;
              team2Stats.tiebreaksLost += 1;
            } else if (set.tieBreak.team2Points > set.tieBreak.team1Points) {
              team2Stats.tiebreaksWon += 1;
              team1Stats.tiebreaksLost += 1;
            }
          }
        }
      }
    }

    // Calculate win rates
    for (const stats of statsMap.values()) {
      const totalMatches = stats.wins + stats.losses;
      stats.winRate =
        totalMatches > 0 ? Math.round((stats.wins / totalMatches) * 100) : 0;
    }

    return statsMap;
  }, [matches]);
}

/**
 * Gets statistics for a single team from the pre-computed map.
 * Use this in components that need stats for a single team.
 */
export function useTeamStatisticsFromMap(
  teamId: string | null | undefined,
  statsMap: Map<string, TeamStatistics>,
): TeamStatistics | null {
  return useMemo(() => {
    if (!teamId) return null;
    return (
      statsMap.get(teamId) ?? {
        teamId,
        wins: 0,
        losses: 0,
        winRate: 0,
        challengesSent: 0,
        challengesReceived: 0,
        gamesWon: 0,
        gamesLost: 0,
        setsWon: 0,
        setsLost: 0,
        tiebreaksWon: 0,
        tiebreaksLost: 0,
        rankingHistory: [],
      }
    );
  }, [teamId, statsMap]);
}
